import styled from 'styled-components';
import { useState } from 'react';
import noop from 'utils/noop';
import { FlexSpacer } from 'ui/flexSpacer';
import { Settings16 } from 'ui/icons/aurora/16px/Settings16';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Body2, colors, spacing } from 'ui/theme';
import { AlertsHistorySlideOver } from 'modules/campaigns/lib/components/AlertsHistorySlideOver';
import { CampaignAlertHistorySource } from 'modules/campaigns/lib/components/AlertsHistorySlideOver/types';
import { LinkButton } from 'ui/linkButton';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';

import { DashboardCard } from '../../Dashboard.styled';
import { ALERTS_GRID_AREA } from '../CampaignsHealthDashboard.constants';
import { RecentCampaignAlertsContents } from './RecentCampaignAlertsContents';
import { MessageMedium } from 'constants/messageMediums';
import { CampaignType } from 'modules/domains/campaign';
import { RecentCampaignAlert } from 'modules/missionControl/types/health';

const parseCampaignType = (apiType: string): CampaignType => {
  const apiTypeToEnumMap: Record<string, CampaignType> = {
    Triggered: CampaignType.ApiTriggered,
    Blast: CampaignType.Blast,
    Eligibility: CampaignType.Eligibility,
    WithAI: CampaignType.WithAI,
    WithExperiment: CampaignType.WithExperiment,
  };

  return apiTypeToEnumMap[apiType] ?? CampaignType.ApiTriggered;
};

const parseMessageMedium = (apiMedium: string): MessageMedium => {
  const apiMediumToEnumMap: Record<string, MessageMedium> = {
    Email: MessageMedium.Email,
    SMS: MessageMedium.SMS,
    InApp: MessageMedium.InApp,
    Push: MessageMedium.Push,
    WebPush: MessageMedium.WebPush,
  };

  return apiMediumToEnumMap[apiMedium] ?? MessageMedium.Email;
};

const StyledDashboardCard = styled(DashboardCard)`
  grid-area: ${ALERTS_GRID_AREA};

  &&& {
    padding: 0;
    height: 100%;
    min-width: ${pxToRem(300)};
  }
`;

const CardTitle = styled(Body2)`
  margin: 0;
  color: ${colors.textSecondary};
`;

export const RecentCampaignAlerts = () => {
  const [selectedAlert, setSelectedAlert] = useState<RecentCampaignAlert | null>(null);

  return (
    <StyledDashboardCard>
      <FlexSpacer
        alignItems="flex-start"
        flexDirection="column"
        gap={spacing.threeQuarter}
        height="100%"
      >
        <FlexSpacer
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          padding={`${spacing.threeQuarter} ${spacing.threeQuarter} 0`}
        >
          <CardTitle>Alerts</CardTitle>

          <Tooltip
            automaticallyAvoidCollisions
            placement={TipPlacement.Right}
            tooltipContent={"Manage project's alerts configuration"}
          >
            <LinkButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.GHOST}
              to="/settings/alerts/campaigns"
            >
              <Settings16 />
            </LinkButton>
          </Tooltip>
        </FlexSpacer>

        <RecentCampaignAlertsContents
          selectedAlert={selectedAlert}
          setSelectedAlert={setSelectedAlert}
        />
      </FlexSpacer>

      {selectedAlert && (
        <AlertsHistorySlideOver
          campaignId={selectedAlert.campaignId}
          messageMedium={parseMessageMedium(selectedAlert.messageMedium as string)}
          campaignType={parseCampaignType(selectedAlert.campaignType as string)}
          campaignState={selectedAlert.campaignState}
          campaignName={selectedAlert.campaignName}
          templateId={selectedAlert.templateId}
          campaignAlertsEnabled={selectedAlert.campaignsAlertEnabled}
          onClose={() => setSelectedAlert(null)}
          onEditAlerts={noop} // Alert overrides is not yet supported
          defaultSelectedAlert={selectedAlert}
          source={CampaignAlertHistorySource.Homepage}
        />
      )}
    </StyledDashboardCard>
  );
};
